import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { EmailSchema } from '../../../utils/schema';
import type { ResponseResult } from '../../../utils/types';
import { CognitoErrorCodesEnum } from '../../../utils/types/eunms';
import type { SignupLabels } from './index';

export type SignupFormValues = {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
};

type Props = {
	onSubmit: (data: any) => Promise<ResponseResult<any>>;
	labels: SignupLabels;
};

export const useSignupForm = ({ onSubmit, labels }: Props) => {
	const schema = React.useMemo(
		() =>
			z.object({
				email: EmailSchema,
				password: z
					.string()
					.min(8, { message: labels.passwordErrors.length })
					.regex(/[A-Z]+/, { message: labels.passwordErrors.uppercase })
					.regex(/[a-z]+/, { message: labels.passwordErrors.lowercase })
					.regex(/[0-9]+/, { message: labels.passwordErrors.number }),
				firstName: z.string().min(1),
				lastName: z.string().min(1),
			}),
		[]
	);

	const {
		formState: { errors, isValid, isSubmitted, isSubmitting, isLoading },
		setError,
		handleSubmit: internalHandleSubmit,
		control,
	} = useForm<SignupFormValues>({
		mode: 'onChange',
		defaultValues: {
			email: '',
			password: '',
			firstName: '',
			lastName: '',
		},
		resolver: zodResolver(schema),
	});

	const submit = React.useCallback(
		async (data: SignupFormValues): Promise<void> => {
			const { error, result } = await onSubmit(data);

			if (error?.code === CognitoErrorCodesEnum.USER_ALREADY_EXISTS) {
				setError('email', { message: error.message });
			}

			if (error?.code === CognitoErrorCodesEnum.INVALID_EMAIL_OR_PASSWORD) {
				setError('password', { message: error.message });
			}
		},
		[onSubmit]
	);

	const handleSubmit = React.useMemo(() => internalHandleSubmit(submit), [internalHandleSubmit, submit]);

	return {
		errors,
		isSubmitted,
		isValid,
		control,
		handleSubmit,
		isLoading: isSubmitting || isLoading,
	};
};
